import React from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Info } from "lucide-react";
import { dateLocale } from "i18n";
import { parseUtc } from "utils/caseDashboard";

// Small building blocks shared by the case dashboard views. Styling follows
// src/v2/views/CLAUDE.md.

export const Card = ({
  title,
  action,
  children,
  className = "",
  bodyClassName = "",
}) => (
  <div className={`rounded-xl border border-[#F3F4F6] bg-white ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#111827]">{title}</h2>
        {action}
      </div>
    )}
    <div className={bodyClassName}>{children}</div>
  </div>
);

export const StatTile = ({ icon: Icon, label, value, hint, tone }) => (
  <div className="rounded-xl border border-[#F3F4F6] bg-white p-4">
    <div className="mb-2 flex items-center gap-1.5">
      {Icon && <Icon size={14} className="text-[#6B7280]" />}
      <span className="text-xs text-[#6B7280]">{label}</span>
    </div>
    <span
      className={`text-[28px] font-bold ${
        tone === "warn" ? "text-[#E07A3A]" : "text-[#111827]"
      }`}
    >
      {value}
    </span>
    {hint && <p className="mt-1 text-[11px] text-[#9CA3AF]">{hint}</p>}
  </div>
);

const DOT = {
  ok: "#10B981",
  warn: "#E07A3A",
  bad: "#DC2626",
  never: "#D1D5DB",
  info: "#6B7280",
};

export const StatusDot = ({ tone = "info", pulse = false, size = 8 }) => (
  <span className="relative inline-flex" style={{ width: size, height: size }}>
    {pulse && (
      <span
        className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
        style={{ backgroundColor: DOT[tone] }}
      />
    )}
    <span
      className="relative inline-flex h-full w-full rounded-full"
      style={{ backgroundColor: DOT[tone] }}
    />
  </span>
);

export const EmptyState = ({ icon: Icon = Info, title, body, action }) => (
  <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#F3F4F6]">
      <Icon size={22} className="text-[#6B7280]" />
    </div>
    <p className="text-sm font-semibold text-[#111827]">{title}</p>
    {body && <p className="mt-1 max-w-sm text-[13px] text-[#6B7280]">{body}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Notice = ({ children, tone = "info" }) => (
  <div
    className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-[13px] ${
      tone === "error"
        ? "bg-[#FEF2F2] text-[#B91C1C]"
        : tone === "warn"
        ? "bg-[#FEF3E7] text-[#9A4A17]"
        : "bg-[#F3F4F6] text-[#374151]"
    }`}
  >
    <AlertCircle size={15} className="mt-0.5 shrink-0" />
    <div>{children}</div>
  </div>
);

/** Shows a query's error / "needs update" state; renders nothing otherwise. */
export const QueryState = ({ query }) => {
  const { t } = useTranslation();
  if (query.missing)
    return <Notice tone="warn">{t("caseDashboard.common.needsUpdate")}</Notice>;
  if (query.error && !query.data)
    return (
      <Notice tone="error">
        {t("caseDashboard.common.queryError", { message: query.error })}
      </Notice>
    );
  return null;
};

export const primaryButtonStyle = {
  background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
};

export const SecondaryButton = ({ children, className = "", ...props }) => (
  <button
    type="button"
    className={`flex items-center justify-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  >
    {children}
  </button>
);

export const PrimaryButton = ({ children, className = "", ...props }) => (
  <button
    type="button"
    className={`flex items-center justify-center gap-1.5 rounded-[10px] px-4 py-2 text-sm font-semibold text-white transition duration-150 hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    style={primaryButtonStyle}
    {...props}
  >
    {children}
  </button>
);

// ── Time formatting (case timestamps are UTC without a zone) ──

export const fmtTime = (value) => {
  const d = parseUtc(value);
  return d
    ? d.toLocaleTimeString(dateLocale("en-GB"), {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "—";
};

export const fmtDateTime = (value) => {
  const d = parseUtc(value);
  return d
    ? d.toLocaleString(dateLocale("en-GB"), {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
};

export const fmtShortMac = (mac) =>
  mac ? String(mac).replace(/:/g, "").slice(-4).toUpperCase() : "—";
